import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Send, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Messages() {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const fetchConvos = useCallback(() => {
    API.get('/messages/conversations').then(r => setConvos(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  useEffect(() => {
    if (!active) return;
    const load = () => API.get(`/messages/${active}`).then(r => setMessages(r.data)).catch(() => {});
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      await API.post('/messages', { receiver_id: active, content: text.trim() });
      setText('');
      const r = await API.get(`/messages/${active}`);
      setMessages(r.data);
      fetchConvos();
    } catch { /* ignore */ }
    setSending(false);
  };

  const activeConvo = convos.find(c => c.user_id === active);

  return (
    <div className="fade-in" data-testid="messages-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope] mb-6">Messages</h1>

      <div className="bg-white rounded-2xl border border-[#EBEBEB] overflow-hidden" style={{ height: 'calc(100vh - 240px)', minHeight: '400px' }}>
        <div className="grid md:grid-cols-3 h-full">
          {/* Conversations list */}
          <div className={`border-r border-[#EBEBEB] overflow-y-auto ${active ? 'hidden md:block' : ''}`} data-testid="conversations-list">
            {convos.length === 0 ? (
              <div className="p-8 text-center text-[#717171] text-sm">No conversations yet</div>
            ) : (
              convos.map(c => (
                <button
                  key={c.user_id}
                  onClick={() => setActive(c.user_id)}
                  className={`w-full text-left p-4 border-b border-[#EBEBEB] hover:bg-[#F7F7F7] transition-colors ${active === c.user_id ? 'bg-[#F7F7F7]' : ''}`}
                  data-testid={`convo-${c.user_id}`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-sm text-[#222] font-[Manrope]">{c.user_name}</p>
                    {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-[#E05A47] text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}
                  </div>
                  <p className="text-xs text-[#717171] mt-1 truncate">{c.last_message}</p>
                </button>
              ))
            )}
          </div>

          {/* Chat area */}
          <div className={`md:col-span-2 flex flex-col ${!active ? 'hidden md:flex' : ''}`}>
            {active ? (
              <>
                <div className="p-4 border-b border-[#EBEBEB] flex items-center gap-3">
                  <button onClick={() => setActive(null)} className="md:hidden text-[#717171]" data-testid="back-to-convos">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <p className="font-bold text-[#222] font-[Manrope]">{activeConvo?.user_name || 'Chat'}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                  {messages.map(m => {
                    const isMine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isMine ? 'bg-[#E05A47] text-white rounded-br-sm' : 'bg-[#F7F7F7] text-[#222] rounded-bl-sm'}`} data-testid={`msg-${m.id}`}>
                          <p className="text-sm">{m.content}</p>
                          <p className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-[#717171]'}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <div className="p-4 border-t border-[#EBEBEB]">
                  <div className="flex gap-2">
                    <Input
                      value={text}
                      onChange={e => setText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSend()}
                      placeholder="Type a message..."
                      className="rounded-full border-[#EBEBEB] h-10 px-4"
                      data-testid="message-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!text.trim() || sending}
                      className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-full w-10 h-10 p-0 btn-press"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#717171] text-sm">Select a conversation</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
